import React, { useState } from "react";
import { Button } from "./ui/Button";
import { motion, AnimatePresence } from "framer-motion";
import {
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Mail,
  Phone,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  MapPin,
} from "lucide-react";

const FAQS = [
  {
    question: "Which plywood is best for kitchens?",
    answer:
      "BWP (Boiling Water Proof) grade plywood is recommended for kitchens because it offers high moisture resistance and long-term durability.",
  },
  {
    question: "How is Croma Ply different from regular plywood?",
    answer:
      "Croma Ply utilizes high-density hardwood cores, advanced resin technology, and undergoes a 72-hour boiling water test to ensure superior strength and longevity compared to standard plywood.",
  },
  {
    question: "Does your plywood come with a warranty?",
    answer:
      "Yes, our premium ranges like Croma Club Plus come with up to 35 years of warranty against borer and termite attacks.",
  },
  {
    question: "Is Croma Ply termite and borer resistant?",
    answer:
      "Absolutely. All our plywood ranges are chemically treated with fixed preservatives to make them resistant to termites, borers, and other wood-destroying organisms.",
  },
];

const OFFICE_LOCATIONS = [
  {
    id: "hoshiarpur",
    city: "Hoshiarpur, Punjab",
    desc: "Village Daulowal, Dasua Road, Hoshiarpur, Punjab. Our advanced manufacturing plant where every Croma Ply sheet is engineered with precision and consistency.",
    email: "support@cromaply.com",
    mobile: ["+91 91849 78666", "+91 98780 55512"],
    work: "01882-276166",
  },
  {
    id: "delhi",
    city: "Delhi, India",
    desc: "Regional corporate office handling north India operations and distribution.",
    email: "delhi@cromaply.com",
  },
  {
    id: "mumbai",
    city: "Mumbai, Maharashtra",
    desc: "Sales and export division catering to western India and international markets.",
    email: "mumbai@cromaply.com",
  },
];

export const ContactPage: React.FC = () => {
  const [activeOffice, setActiveOffice] = useState("hoshiarpur");
  const [activeFaq, setActiveFaq] = useState(0);

  return (
    <div className="pt-24 lg:pt-32 pb-0 bg-[#FAFAFA] min-h-screen px-4 lg:px-12 font-raleway">
      {/* --- Section 1: Contact Form & Info --- */}
      <section className="">
          <div className="container mx-auto flex flex-col lg:flex-row gap-2 items-start justify-between pb-4 max-w-full lg:max-w-[70%]">
            {/* Left Side: Contact Info - more space */}
            <div className="w-full lg:flex-1 lg:min-w-0">
              <h1
                className="text-3xl md:text-heading-large font-open-sans-hebrew font-bold mb-6 bg-gradient-to-r
         from-[#e20016] from-[0%]
         via-[#712f59] via-[40%]
         to-[#005d9c] to-[100%] bg-clip-text text-transparent"
              >
                Contact us today
              </h1>
              <p className="text-gray-500 text-body mb-5 ">
                Get expert guidance for choosing the right plywood for your
                project. Our team is here to help you with product details,
                applications, and technical support.
              </p>

              <div className="flex gap-6 mb-8">
                <a
                  href="https://www.facebook.com/cromaply"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-black hover:text-croma-blue transition-colors"
                >
                  <Facebook size={20} strokeWidth={1.5} />
                </a>
                <button
                  type="button"
                  className="text-black hover:text-croma-blue transition-colors"
                >
                  <Twitter size={20} strokeWidth={1.5} />
                </button>
                <a
                  href="https://www.instagram.com/cromaply?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw=="
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-black hover:text-croma-blue transition-colors"
                >
                  <Instagram size={20} strokeWidth={1.5} />
                </a>
                <a
                  href="https://in.linkedin.com/company/virgo-panel-product"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-black hover:text-croma-blue transition-colors"
                >
                  <Linkedin size={20} strokeWidth={1.5} />
                </a>
              </div>

              <p
                className="text-heading-sub font-open-sans-hebrew font-bold bg-gradient-to-r
         from-[#e20016] from-[0%]
         via-[#712f59] via-[40%]
         to-[#005d9c] to-[100%] bg-clip-text text-transparent mb-2 text-2xl md:text-[1.7rem]"
              >
                More contact details
              </p>
              <p className="text-body text-gray-500 ">
                Reach out to us directly for product inquiries, dealer support,
                or technical assistance.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3 pt-4">
                <div className="p-4 rounded-lg bg-white flex items-start gap-3 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-center flex-shrink-0 mt-1">
                    <MapPin
                      size={18}
                      className="text-[#e20016]"
                      strokeWidth={1.5}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="block text-body text-gray-400 mb-0.5 text-xs">
                      Office address
                    </span>
                    <p className="text-body font-medium text-gray-900 leading-snug">
                      Village Daulowal, Dasua Road, Hoshiarpur, Punjab
                    </p>
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-white flex items-start gap-3 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-center flex-shrink-0 mt-1">
                    <Phone
                      size={18}
                      className="text-[#e20016]"
                      strokeWidth={1.5}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="block text-body text-gray-400 mb-1 text-xs">
                      Mobile numbers
                    </span>
                    <div className="flex items-center gap-2 flex-wrap">
                      <a
                        href="tel:+919184978666"
                        className="text-body font-medium text-gray-900 hover:text-[#005d9c] transition-colors"
                      >
                        +91 91849 78666
                      </a>
                      <span className="text-gray-300">•</span>
                      <a
                        href="tel:+919878055512"
                        className="text-body font-medium text-gray-900 hover:text-[#005d9c] transition-colors"
                      >
                        +91 98780 55512
                      </a>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-white flex items-start gap-3 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-center flex-shrink-0 mt-1">
                    <Phone
                      size={18}
                      className="text-[#e20016]"
                      strokeWidth={1.5}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="block text-body text-gray-400 mb-0.5 text-xs">
                      Work number
                    </span>
                    <a
                      href="tel:+9101882276166"
                      className="text-body font-medium text-gray-900 hover:text-[#005d9c] transition-colors"
                    >
                      01882-276166
                    </a>
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-white flex items-start gap-3 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-center flex-shrink-0 mt-1">
                    <Mail size={18} className="text-[#e20016]" strokeWidth={1.5} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="block text-body text-gray-400 mb-0.5 text-xs">
                      Email address
                    </span>
                    <a
                      href="mailto:support@cromaply.com"
                      className="text-body font-medium text-gray-900 hover:text-[#005d9c] transition-colors break-all"
                    >
                      support@cromaply.com
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side: Form - single column fields */}
            <div className="bg-white rounded-[24px] p-4 shadow-sm w-full lg:w-[420px] lg:flex-shrink-0">
              <p
                className="text-heading-sub font-open-sans-hebrew font-bold bg-gradient-to-r
         from-[#e20016] from-[0%]
         via-[#712f59] via-[40%]
         to-[#005d9c] to-[100%] bg-clip-text text-transparent mb-2 text-2xl md:text-[1.7rem]"
              >
                Personal information
              </p>
              <p className="text-body text-gray-500 mb-4 text-xs">
                Share your basic details so our team can contact you with the
                right solution.
              </p>

              <form className="space-y-6 flex flex-col h-full">
                <div className="flex flex-col gap-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-body font-medium text-gray-700 ml-1">
                        First name
                      </label>
                      <input
                        type="text"
                        placeholder="ex. Sourav"
                        className="w-full bg-[#FAFAFA] border-none rounded-[8px] px-2 py-2 text-body focus:ring-1 focus:ring-gray-200 placeholder-gray-400 outline-none text-gray-900"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-body font-medium text-gray-700 ml-1">
                        Last name
                      </label>
                      <input
                        type="text"
                        placeholder="ex. Mehra"
                        className="w-full bg-[#FAFAFA] border-none rounded-[8px] px-2 py-2 text-body focus:ring-1 focus:ring-gray-200 placeholder-gray-400 outline-none text-gray-900"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-body font-medium text-gray-700 ml-1">
                        Email address
                      </label>
                      <input
                        type="email"
                        placeholder="example@youremail.com"
                        className="w-full bg-[#FAFAFA] border-none rounded-[8px] px-2 py-2 text-body focus:ring-1 focus:ring-gray-200 placeholder-gray-400 outline-none text-gray-900"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-body font-medium text-gray-700 ml-1">
                        Phone number
                      </label>
                      <input
                        type="tel"
                        placeholder="(123) 456 - 7890"
                        className="w-full bg-[#FAFAFA] border-none rounded-[8px] px-2 py-2 text-body focus:ring-1 focus:ring-gray-200 placeholder-gray-400 outline-none text-gray-900"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-body font-medium text-gray-700 ml-1">
                    What plywood type do you need help with?
                  </label>
                  <p className="text-body text-gray-400 mb-2 ml-1 text-xs">
                    Please choose the plywood category you're interested in so
                    we can assist you better.
                  </p>
                  <div className="relative">
                    <select className="w-full bg-[#FAFAFA] border-none rounded-[8px] px-2 py-2 text-body focus:ring-1 focus:ring-gray-200 text-gray-500 outline-none appearance-none cursor-pointer">
                      <option>Need Help....</option>
                      <option>Marine Grade</option>
                      <option>BWP Grade</option>
                      <option>Fire Retardant</option>
                    </select>
                    <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-body font-medium text-gray-700 ml-1">
                    Message
                  </label>
                  <p className="text-body text-gray-400 mb-2 ml-1 text-xs">
                    Tell us about your project or plywood requirements, and
                    we'll get back to you shortly.
                  </p>
                  <textarea
                    rows={3}
                    placeholder="Write your message here..."
                    className="w-full bg-[#FAFAFA] border-none rounded-[8px] px-2 py-2 text-body focus:ring-1 focus:ring-gray-200 placeholder-gray-400 outline-none resize-none"
                  ></textarea>
                </div>

                <div className="">
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 bg-[#C9252B] hover:bg-red-700 transition-colors text-white px-6 py-3 rounded-lg text-body font-medium"
                  >
                    Send message <ArrowRight size={16} />
                  </button>
                </div>
              </form>
            </div>
          </div>
        </section>

      {/* --- Section 2: Offices --- */}
      <section className="bg-white pb-32 pt-14">
          <div className="container mx-auto px-6 mb-16 text-center max-w-full lg:max-w-[70%]">
            <h2
              className="text-3xl md:text-heading-large font-open-sans-hebrew font-bold mb-4 bg-gradient-to-r
         from-[#e20016] from-[0%]
         via-[#712f59] via-[40%]
         to-[#005d9c] to-[100%] bg-clip-text text-transparent"
            >
              Visit Our Offices To Explore Premium{" "}
              <br className="hidden md:block" />
              Plywood Solutions
            </h2>
            <p className="text-gray-500 text-body max-w-xl mx-auto">
              Explore our regional offices to meet our team, view plywood
              samples, and get expert guidance for your next project.
            </p>
          </div>

          <div className="container mx-auto px-4 sm:px-6 max-w-full lg:max-w-[70%]">
            <div className="relative w-full rounded-[16px] sm:rounded-[24px] lg:rounded-[32px] overflow-hidden flex flex-col lg:block lg:h-[650px]">
              <img
                src="/assets/images/office-contact-bg.webp"
                alt="Modern Office In Forest"
                className="w-full h-[300px] lg:h-full object-cover lg:absolute lg:inset-0"
              />
              <div className="absolute inset-0 bg-black/10 hidden lg:block"></div>

              {/* Location Accordion Overlay */}
              <div className="w-full lg:w-[500px] lg:absolute lg:left-12 lg:bottom-12 lg:max-h-[calc(100%-96px)] overflow-y-auto">
                <div className="bg-[#1A1A1A] lg:bg-[#1A1A1A]/70 lg:backdrop-blur-xl border-none lg:border lg:border-white/10 lg:rounded-[24px] p-6 lg:p-8 text-white shadow-none lg:shadow-2xl">
                  {OFFICE_LOCATIONS.map((loc, index) => {
                    const isActive = activeOffice === loc.id;
                    return (
                      <div key={loc.id}>
                        <div
                          className={`transition-all duration-300 ${isActive ? "pb-0" : "py-0.5 sm:py-1"
                            }`}
                        >
                          <div
                            className="flex items-center justify-between cursor-pointer group gap-2 sm:gap-3"
                            onClick={() =>
                              setActiveOffice(isActive ? "" : loc.id)
                            }
                          >
                            <h3
                              className={`font-open-sans-hebrew font-bold transition-all duration-300 ${isActive
                                ? "text-base sm:text-lg md:text-heading-sub mb-1 sm:mb-2"
                                : "text-base sm:text-lg md:text-heading-sub text-white/90 group-hover:text-white"
                                }`}
                            >
                              {loc.city}
                            </h3>
                            <button
                              className={`w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 rounded-full flex items-center justify-center transition-all duration-300 flex-shrink-0 ${isActive
                                ? "bg-white text-black rotate-0"
                                : "border border-white/40 text-white hover:bg-white/10 -rotate-0"
                                }`}
                            >
                              {isActive ? (
                                <ChevronUp size={14} className="sm:w-4 sm:h-4 md:w-[18px] md:h-[18px]" />
                              ) : (
                                <ChevronDown size={14} className="sm:w-4 sm:h-4 md:w-[18px] md:h-[18px]" />
                              )}
                            </button>
                          </div>

                          <AnimatePresence>
                            {isActive && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden"
                              >
                                <div className="pt-1.5 sm:pt-2 pb-1">
                                  <p className="text-gray-300 text-sm sm:text-body mb-3 sm:mb-4">
                                    {loc.desc}
                                  </p>
                                  {loc.mobile && (
                                    <div className="space-y-1.5 sm:space-y-2 mb-3 sm:mb-4">
                                      {loc.mobile.map((num, idx) => (
                                        <div
                                          key={idx}
                                          className="flex items-center gap-2 sm:gap-3 text-white text-sm sm:text-body font-medium"
                                        >
                                          <Phone size={14} className="sm:w-4 sm:h-4 flex-shrink-0" />
                                          <a
                                            href={`tel:+91${num.replace(
                                              /\s/g,
                                              ""
                                            )}`}
                                            className="hover:text-gray-300 transition-colors break-all"
                                          >
                                            {num}
                                          </a>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  {loc.work && (
                                    <div className="flex items-center gap-2 sm:gap-3 text-white text-sm sm:text-body font-medium mb-3 sm:mb-4">
                                      <Phone size={14} className="sm:w-4 sm:h-4 flex-shrink-0" />
                                      <span className="break-all">Work: {loc.work}</span>
                                    </div>
                                  )}
                                  <div className="flex items-center gap-2 sm:gap-3 text-white text-sm sm:text-body font-medium">
                                    <Mail size={14} className="sm:w-4 sm:h-4 flex-shrink-0" />
                                    <a
                                      href={`mailto:${loc.email}`}
                                      className="hover:text-gray-300 transition-colors break-all"
                                    >
                                      {loc.email}
                                    </a>
                                  </div>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>

                        {/* Divider */}
                        {index !== OFFICE_LOCATIONS.length - 1 && (
                          <div className="h-[1px] bg-white/20 my-3 sm:my-4 md:my-6 w-full"></div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

      {/* --- Section 3: FAQ --- */}
      <section className="py-24 bg-black text-white">
          <div className="container mx-auto px-6">
            <div className="flex flex-col lg:flex-row gap-16 lg:gap-32">
              {/* Left: Content */}
              <div className="lg:w-1/3">
                <h2
                  className="text-3xl md:text-heading-large font-open-sans-hebrew font-bold mb-6 bg-gradient-to-r
         from-croma-blue from-[0%]
         to-white to-[100%] bg-clip-text text-transparent"
                >
                  Have questions?
                </h2>
                <p className="text-gray-400 text-body mb-10">
                  We're here to help you understand our products,
                  certifications, and the best plywood options for your project.
                </p>
                <Button variant="red">Let's Discuss</Button>
              </div>

              {/* Right: Accordion */}
              <div className="lg:w-2/3 flex flex-col">
                {FAQS.map((faq, index) => (
                  <div
                    key={index}
                    className="border-b border-white/20 py-8 first:pt-0"
                  >
                    <button
                      onClick={() =>
                        setActiveFaq(activeFaq === index ? -1 : index)
                      }
                      className="w-full flex items-center justify-between text-left group"
                    >
                      <span className="text-heading-sub font-open-sans-hebrew font-bold">
                        {faq.question}
                      </span>
                      <div
                        className={`w-8 h-8 rounded-full border border-white/30 flex items-center justify-center transition-all duration-300 ${activeFaq === index
                          ? "rotate-180 bg-[#e20016] text-white border-[#e20016]"
                          : "group-hover:border-[#e20016] group-hover:bg-[#e20016] group-hover:text-white"
                          }`}
                      >
                        <ChevronDown size={16} />
                      </div>
                    </button>
                    <AnimatePresence>
                      {activeFaq === index && (
                        <motion.div
                          initial={{ height: 0, opacity: 0, marginTop: 0 }}
                          animate={{
                            height: "auto",
                            opacity: 1,
                            marginTop: 16,
                          }}
                          exit={{ height: 0, opacity: 0, marginTop: 0 }}
                          className="overflow-hidden"
                        >
                          <p className="text-gray-400 text-body pr-10">
                            {faq.answer}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

      {/* --- Section 4: Instagram --- */}
      <section className="py-10 lg:py-24 bg-white">
          <div className="container mx-auto px-6 text-center mb-8 lg:mb-16 max-w-full lg:max-w-[70%]">
            <h2
              className="text-3xl md:text-heading-large font-open-sans-hebrew font-bold mb-4 bg-gradient-to-r
         from-[#e20016] from-[0%]
         via-[#712f59] via-[40%]
         to-[#005d9c] to-[100%] bg-clip-text text-transparent"
            >
              Connect With Us On Instagram
            </h2>
            <p className="text-gray-500 text-body max-w-xl mx-auto">
              Discover project highlights, product applications, and
              behind-the-scenes craftsmanship directly from our Instagram feed.
            </p>
          </div>

          <div className="container mx-auto px-6 mb-8 lg:mb-12 max-w-full lg:max-w-[70%]">
            <div className="flex flex-col md:flex-row gap-2 lg:gap-6">
              {/* First Section - 4 images in 2-column grid */}
              <div className="w-full md:w-1/2">
                <div className="grid grid-cols-2 gap-2 lg:gap-6 h-full">
                  <div className="rounded-[16px] lg:rounded-[24px] overflow-hidden relative group aspect-square">
                    <img
                      src="/assets/images/instagram/insta-img1.webp"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                      alt="Insta 1"
                    />
                  </div>

                  <div className="rounded-[16px] lg:rounded-[24px] overflow-hidden relative group aspect-square">
                    <img
                      src="/assets/images/instagram/insta-img2.webp"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                      alt="Insta 2"
                    />
                  </div>

                  <div className="rounded-[16px] lg:rounded-[24px] overflow-hidden relative group aspect-square">
                    <img
                      src="/assets/images/instagram/insta-img3.webp"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                      alt="Insta 3"
                    />
                  </div>

                  <div className="rounded-[16px] lg:rounded-[24px] overflow-hidden relative group aspect-square">
                    <img
                      src="/assets/images/instagram/insta-img4.webp"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                      alt="Insta 4"
                    />
                  </div>
                </div>
              </div>

              {/* Second Section - Single image full width */}
              <div className="w-full md:w-1/2">
                <div className="rounded-[16px] lg:rounded-[24px] overflow-hidden relative group h-full min-h-[250px] lg:min-h-[600px]">
                  <img
                    src="/assets/images/instagram/insta-img5.webp"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    alt="Insta 5"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <a
              href="https://www.instagram.com/cromaply?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw=="
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="red">Explore our Instagram</Button>
            </a>
          </div>
        </section>
    </div>
  );
};
